import { groupBy, reduce } from 'lodash';

import {
  ensureSystemId,
  getZoneIdentity,
  panelDeviceName,
  ssrLabelToIndex,
} from '../setting';

/**
 * The system-identification form values the zone bodies need:
 * `{ locationName, specificLocation, civicAddress, numOfUOS, numOfSSR,
 * numSwitches, UOSName, UOSDelete }`.
 * @typedef {object} SystemIdentificationForm
 */

const zoneFields = ({
  locationName,
  specificLocation,
  civicAddress,
  numOfUOS,
  numOfSSR,
  numSwitches,
}) => ({
  zone_name: locationName,
  specific_address: specificLocation,
  zone_address: civicAddress,
  uos_panel_number: Number(numOfUOS),
  ssr_quantity: Number(numOfSSR),
  switches_number: Number(numSwitches),
});

// Assigns each switch its system id (`ensureSystemId` stores it on the row as
// `_randomId`), which `ssrs` then reads: build `switch_panels` first.
const switchPanels = (switches) =>
  switches.map((switchInfo) => ({
    system_id: ensureSystemId(switchInfo),
    name: switchInfo.switchName?.toUpperCase(),
    switch_size: switchInfo.switchSize?.slice(1),
    system_heating: switchInfo.heatingSys
      ?.split('/')
      .map((system) => system.toUpperCase()),
    gas_type: switchInfo.gasType?.toUpperCase() || null,
    ssr_uts: [
      ...new Set(
        switchInfo.selectedSSR?.map((entry) =>
          ssrLabelToIndex(Object.keys(entry)[0]),
        ),
      ),
    ],
    ssr_rating: switchInfo.ssrRating?.slice(0, -5),
    application: switchInfo.application?.toUpperCase(),
  }));

// One entry per SSR of the panel, with the heaters of every switch on it.
const ssrs = (switches) => {
  const perSwitch = switches.flatMap((switchInfo) => {
    const byLabel = groupBy(
      switchInfo.selectedSSR,
      (entry) => Object.keys(entry)[0],
    );
    return Object.keys(byLabel).map((label) => {
      const heaters = byLabel[label].flatMap((entry) =>
        Object.values(entry)[0]
          .specs?.map((spec) => spec.partNumber)
          .filter((partNumber) => partNumber),
      );
      return {
        no: ssrLabelToIndex(label),
        tc: +Object.values(byLabel[label][0])[0].thermoCouple?.slice(-2),
        switch_system_id: heaters.length > 0 ? switchInfo._randomId : null,
        heaters,
      };
    });
  });

  return Object.values(
    reduce(
      perSwitch,
      (bySsr, entry) => {
        if (!bySsr[entry.no]) {
          bySsr[entry.no] = { ...entry, heaters: [] };
        }
        bySsr[entry.no].heaters.push(...entry.heaters);
        return bySsr;
      },
      {},
    ),
  );
};

// A panel's name built from its switches. Starts from an empty array, so the
// first name gets a leading '/' (as the page always sent it).
const nameFromSwitches = (switches) =>
  switches.reduce(
    (name, { switchName, switchSize, application, heatingSys, gasType }) => {
      const prefix = `${name}${name && '/'}${switchName} ${switchSize} ${application}`;
      if (heatingSys === 'tgs/tes') {
        const [gas, electric] = heatingSys.split('/');
        return `${prefix} ${gas}:${gasType}-${electric}`;
      }
      if (heatingSys === 'tgs') {
        return `${prefix} ${heatingSys}:${gasType}`;
      }
      return `${prefix} ${heatingSys}`;
    },
    [],
  );

const panelType = (switches) =>
  switches?.every((switchInfo) => switchInfo.heatingSys.includes('ess'))
    ? 'switch'
    : 'blower';

/**
 * Body for creating a zone. `switches` are either grouped by panel already
 * (an array of arrays) or loose rows, grouped here by their `UOS`.
 * @param {Array} switches
 * @param {SystemIdentificationForm} form
 */
export const systemIdentificationCreatePayload = (switches, form) => {
  const panels = Array.isArray(switches[0])
    ? switches
    : Object.values(groupBy(switches.flat(), (switchInfo) => switchInfo.UOS));

  return {
    ...zoneFields(form),
    uos_panels: panels
      .filter((panel) => panel?.length > 0)
      .map((panel) => ({
        device_name:
          panelDeviceName(form.UOSName, panel[0]?.UOS).toUpperCase() ||
          nameFromSwitches(panel),
        type: panelType(panel),
        switch_panels: switchPanels(panel),
        ssr: ssrs(panel),
      })),
  };
};

/**
 * Body for updating (or deleting from) a stored zone: its identity, the
 * panels the operator removed, and every panel left.
 * @param {Array} switches
 * @param {SystemIdentificationForm} form
 */
export const systemIdentificationUpdatePayload = (switches, form) => {
  const byPanel = groupBy(switches.flat(), (switchInfo) => switchInfo.UOS);
  const panelNumbers = Object.keys(byPanel);

  return {
    ...zoneFields(form),
    ...getZoneIdentity(switches, form.UOSDelete),
    uos_delete: (form.UOSDelete ?? [])
      .filter((removed) => !panelNumbers.includes(removed.UOS))
      .map(({ type, device_mac }) => ({ type, device_mac })),
    uos_panels: Object.values(byPanel).map((panel) => ({
      device_mac: panel.find(({ deviceMac }) => deviceMac)?.deviceMac,
      device_name: panelDeviceName(form.UOSName, panel[0]?.UOS),
      type: panelType(panel),
      switch_panels: switchPanels(panel),
      ssr: ssrs(panel),
    })),
  };
};
